import {createStore, del, entries, get, set, clear} from 'idb-keyval'
import type {Validator} from 'typebox/compile'
import type {PersistedQuery} from './types'

// TODO: should this actually be using '@github-ui/stats' ?
import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'
import {getCurrentUserLogin} from '@github-ui/client-env'

type Props = {
  validator: Validator
  sendAnalyticsEvent: SendAnalyticsEventFunction
  storeName?: string
}

export const DBNAME_PREFIX = 'offline_cache::'
export const getDbName = () => DBNAME_PREFIX + getCurrentUserLogin()

export function createSafeIndexedDbPersister({validator, sendAnalyticsEvent, storeName = 'queries'}: Props) {
  const idbStore = createStore(getDbName(), storeName)
  return {
    async getItem(key: string) {
      try {
        const item = await get<PersistedQuery>(key, idbStore)

        // only hydrate persisted query data
        if (!item?.state?.data) return

        // Validate the data inside the persisted query
        const valid = validator.Check(item.state.data)
        if (!valid) {
          sendAnalyticsEvent('offline_cache.stale_structure', key.toString())
          return
        }
        sendAnalyticsEvent('offline_cache.get_item', key.toString())
        return item
      } catch (error) {
        // "Re-throw the caught exception" according to https://github.com/github/github-ui/blob/main/packages/failbot/failbot.ts
        throw error
      }
    },
    setItem(key: string, value: unknown) {
      return set(key, value, idbStore)
    },
    removeItem(key: string) {
      sendAnalyticsEvent('offline_cache.remove_item', key.toString())
      return del(key, idbStore)
    },
    async entries() {
      const things = await entries(idbStore)
      return things as Array<[string, PersistedQuery]>
    },
  }
}

export async function clearIndexedDBPersisterData() {
  try {
    const databases = await window?.indexedDB?.databases?.()
    if (!databases) return

    // For each database matching our prefix, clear all its stores
    const clearPromises = databases
      .filter(db => db.name?.startsWith(DBNAME_PREFIX))
      .map(async db => {
        if (!db.name) return

        try {
          // Open the database to get all store names
          const openRequest = indexedDB.open(db.name)
          const database = await new Promise<IDBDatabase>((resolve, reject) => {
            openRequest.onsuccess = () => resolve(openRequest.result)
            openRequest.onerror = () => reject(openRequest.error)
          })

          // Get all object store names in this database
          const storeNames = Array.from(database.objectStoreNames)
          database.close()

          // Clear each store using idb-keyval
          const storeClearPromises = storeNames.map(async storeName => {
            if (!db.name) return
            try {
              const store = createStore(db.name, storeName)
              await clear(store)
            } catch {
              // Silently ignore errors when clearing individual stores
            }
          })

          await Promise.all(storeClearPromises)
        } catch {
          // Silently ignore errors when clearing individual databases
        }
      })

    await Promise.allSettled(clearPromises)
  } catch {
    // ignore failure to clear IndexedDB
  }
}
